/**
 * Gestionnaire dédié pour le cookie user-data
 * Assure la synchronisation, la validation et la récupération du cookie user-data
 */

import { TokenManager } from './token-manager';
import { JwtValidator } from './jwt-validator';

// Nom du cookie principal
const USER_DATA_COOKIE_NAME = 'user-data';

// Durée de vie du cookie (7 jours)
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

export interface UserData {
  id: number | string;
  username: string;
  email: string;
  is_active: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  first_name?: string;
  last_name?: string;
  [key: string]: any;  // Pour toute autre propriété potentielle
}

export const UserDataManager = {
  /**
   * Récupère et valide le cookie user-data
   * Effectue une récupération à partir de différentes sources si nécessaire
   */
  getUserData(): UserData | null {
    try {
      console.log('🔍 Vérification du cookie user-data');
      
      // Essayer d'abord de récupérer depuis le cookie
      const userData = this.getUserDataFromCookie();
      
      if (userData) {
        console.log('✅ Cookie user-data récupéré et validé');
        return userData;
      }
      
      console.log('⚠️ Cookie user-data manquant ou invalide, tentative de récupération...');
      
      // Essayer de récupérer depuis localStorage
      const localStorageData = this.getUserDataFromLocalStorage();
      if (localStorageData) {
        console.log('✅ Données utilisateur récupérées depuis localStorage');
        // Restaurer le cookie à partir des données localStorage
        this.setUserDataCookie(localStorageData);
        return localStorageData;
      }
      
      // Essayer de récupérer depuis le token JWT
      const jwtData = this.getUserDataFromJwt();
      if (jwtData) {
        console.log('✅ Données utilisateur extraites du token JWT');
        // Sauvegarder les données dans le cookie et localStorage
        this.setUserDataCookie(jwtData);
        this.saveUserDataToLocalStorage(jwtData);
        return jwtData;
      }
      
      console.warn('❌ Impossible de récupérer les données utilisateur depuis aucune source');
      return null;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des données utilisateur:', error);
      return null;
    }
  },
  
  /**
   * Récupère le cookie user-data et le valide
   */
  getUserDataFromCookie(): UserData | null {
    try {
      const userDataCookie = document.cookie
        .split(';')
        .find(c => c.trim().startsWith(`${USER_DATA_COOKIE_NAME}=`));
      
      if (!userDataCookie) {
        return null;
      }
      
      // Extraire et décoder la valeur du cookie
      const cookieValue = userDataCookie.split('=')[1];
      if (!cookieValue) {
        return null;
      }
      
      const decodedValue = decodeURIComponent(cookieValue);
      const parsedValue = JSON.parse(decodedValue);
      
      // Valider le contenu du cookie
      if (this.validateUserData(parsedValue)) {
        return parsedValue;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération/parsing du cookie user-data:', error);
      return null;
    }
  },
  
  /**
   * Récupère les données utilisateur depuis localStorage
   */
  getUserDataFromLocalStorage(): UserData | null {
    try {
      // Vérifier différentes clés possibles où les données pourraient être stockées
      const keys = [
        'userProfile',
        'sentinel_user_profile',
        'user-data',
        'userData'
      ];
      
      for (const key of keys) {
        const data = localStorage.getItem(key);
        if (data) {
          try {
            const parsedData = JSON.parse(data);
            if (this.validateUserData(parsedData)) {
              return parsedData;
            }
          } catch (e) {
            console.warn(`⚠️ Erreur lors du parsing des données de ${key}:`, e);
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération depuis localStorage:', error);
      return null;
    }
  },
  
  /**
   * Extrait les données utilisateur du token JWT
   */
  getUserDataFromJwt(): UserData | null {
    try {
      const accessToken = TokenManager.getAccessToken();
      if (!accessToken) {
        return null;
      }
      
      const decodedToken = TokenManager.decodeToken(accessToken);
      if (!decodedToken) {
        return null;
      }
      
      // Construire un objet UserData à partir des claims du token
      const userData: UserData = {
        id: decodedToken.user_id || decodedToken.sub || 0,
        username: decodedToken.username || decodedToken.preferred_username || decodedToken.name || '',
        email: decodedToken.email || '',
        is_active: true,
        first_name: decodedToken.first_name || decodedToken.given_name || '',
        last_name: decodedToken.last_name || decodedToken.family_name || '',
        is_staff: decodedToken.is_staff === true,
        is_superuser: decodedToken.is_superuser === true
      };
      
      // Valider que les données extraites sont utilisables
      if (this.validateUserData(userData)) {
        return userData;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Erreur lors de l\'extraction des données depuis le JWT:', error);
      return null;
    }
  },
  
  /**
   * Valide que les données utilisateur contiennent les champs essentiels et sont correctement formatées
   */
  validateUserData(data: any): boolean {
    if (!data || typeof data !== 'object') {
      console.warn('⚠️ Données utilisateur nulles ou non-objet');
      return false;
    }
    
    // Vérifier les champs essentiels
    const requiredFields = ['id', 'username', 'email', 'is_active'];
    for (const field of requiredFields) {
      if (data[field] === undefined) {
        console.warn(`⚠️ Champ ${field} manquant dans les données utilisateur`);
        return false;
      }
    }
    
    // Vérification spécifique pour chaque champ clé
    // 1. Validation de l'ID
    if (!(typeof data.id === 'number' || typeof data.id === 'string')) {
      console.warn('⚠️ ID utilisateur invalide (doit être un nombre ou une chaîne)');
      return false;
    }
    
    // 2. Validation du nom d'utilisateur
    if (typeof data.username !== 'string' || data.username.trim() === '') {
      console.warn('⚠️ Nom d\'utilisateur invalide');
      return false;
    }
    
    // 3. Validation de l'email (format basique)
    if (typeof data.email !== 'string' || data.email.trim() === '' || !data.email.includes('@')) {
      console.warn('⚠️ Email invalide');
      return false;
    }
    
    // 4. Vérifier que l'utilisateur est actif
    if (data.is_active !== true) {
      console.warn('⚠️ Compte utilisateur inactif');
      return false;
    }
    
    // 5. Vérification des valeurs booléennes
    if (data.is_staff !== undefined && typeof data.is_staff !== 'boolean') {
      console.warn('⚠️ Valeur is_staff non booléenne');
      return false;
    }
    
    if (data.is_superuser !== undefined && typeof data.is_superuser !== 'boolean') {
      console.warn('⚠️ Valeur is_superuser non booléenne');
      return false;
    }
    
    // Journaliser le succès (en développement uniquement)
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Données utilisateur validées avec succès:', {
        id: typeof data.id,
        username: data.username ? 'valide' : 'invalide',
        email: data.email ? 'valide' : 'invalide',
        is_active: data.is_active
      });
    }
    
    return true;
  },
  
  /**
   * Enregistre les données utilisateur dans le cookie user-data
   */
  setUserDataCookie(userData: UserData): void {
    try {
      const userDataJson = JSON.stringify(userData);
      const encodedValue = encodeURIComponent(userDataJson);
      
      // Paramètres du cookie
      const secure = window.location.protocol === 'https:';
      const sameSite = 'Lax';
      
      // Définir le cookie avec une expiration de 7 jours
      document.cookie = `${USER_DATA_COOKIE_NAME}=${encodedValue}; path=/; max-age=${COOKIE_MAX_AGE}; ${secure ? 'Secure;' : ''} SameSite=${sameSite}`;
      
      // Ajouter le domaine si nécessaire pour les environnements multi-domaines
      if (window.location.hostname.includes('cyber-threat-consulting.com')) {
        document.cookie = `${USER_DATA_COOKIE_NAME}=${encodedValue}; path=/; max-age=${COOKIE_MAX_AGE}; ${secure ? 'Secure;' : ''} SameSite=${sameSite}; domain=.cyber-threat-consulting.com`;
      }
      
      console.log('✅ Cookie user-data mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la définition du cookie user-data:', error);
    }
  },
  
  /**
   * Enregistre les données utilisateur dans localStorage pour plus de résilience
   */
  saveUserDataToLocalStorage(userData: UserData): void {
    try {
      const userDataJson = JSON.stringify(userData);
      
      // Sauvegarder sous différentes clés pour la compatibilité
      localStorage.setItem('userProfile', userDataJson);
      localStorage.setItem('sentinel_user_profile', userDataJson);
      localStorage.setItem('user-data', userDataJson);
      
      console.log('✅ Données utilisateur sauvegardées dans localStorage');
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde dans localStorage:', error);
    }
  },
  
  /**
   * Synchronise le cookie user-data avec les autres sources d'information
   * Garantit que le cookie est toujours présent et valide
   */
  synchronizeUserData(): void {
    console.log('🔄 Synchronisation des données utilisateur...');
    
    // Récupérer les données utilisateur (cette méthode effectue déjà les récupérations et synchronisations nécessaires)
    const userData = this.getUserData();
    
    if (userData) {
      console.log('✅ Données utilisateur synchronisées avec succès');
    } else {
      console.warn('⚠️ Impossible de synchroniser les données utilisateur - aucune source valide trouvée');
    }
  },
  
  /**
   * Efface toutes les données utilisateur (cookies et localStorage)
   */
  clearUserData(): void {
    try {
      // Supprimer le cookie
      document.cookie = `${USER_DATA_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
      
      // Si on est sur le domaine cyber-threat-consulting.com, supprimer aussi avec le domaine spécifié
      if (window.location.hostname.includes('cyber-threat-consulting.com')) {
        document.cookie = `${USER_DATA_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.cyber-threat-consulting.com`;
      }
      
      // Supprimer les données du localStorage
      localStorage.removeItem('userProfile');
      localStorage.removeItem('sentinel_user_profile');
      localStorage.removeItem('user-data');
      localStorage.removeItem('userData');
      
      console.log('✅ Données utilisateur effacées avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la suppression des données utilisateur:', error);
    }
  }
};

export default UserDataManager;
